import React, { useLayoutEffect, useRef } from 'react';

/**
 * A one-value text field that wraps and grows like the text it stands in for.
 *
 * Slide editors drew their questions and cover titles as `<input type="text">`
 * at display size, and an input never wraps: a twelve-word question scrolled
 * sideways and read in the builder as cut off mid-word, while the student
 * view, which renders a wrapping heading, was fine. This keeps the input's
 * single-value semantics (Enter does nothing, pasted line breaks become
 * spaces) and only changes how the text lays out.
 */
type Props = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value' | 'rows'> & {
  value: string;
  onChange: (value: string) => void;
};

export function WrappingInput({ value, onChange, className = '', style, onKeyDown, ...rest }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\s*\n+\s*/g, ' '))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault();
        onKeyDown?.(e);
      }}
      className={`resize-none overflow-hidden block ${className}`}
      style={style}
      {...rest}
    />
  );
}
